import React from "react";
import { useNavigate } from "react-router-dom";

/**
 * Three banner cards proving the studio's capabilities.
 * Each banner has an image, title, short text and a "подробнее" link
 * pointing to: a section anchor (#id), an internal article (/article/slug)
 * or an external URL — editable in admin.
 */
export default function SkillsProof({ site }) {
  const items = site.skills_proof || [];
  const navigate = useNavigate();

  const go = (link) => {
    if (!link) return;
    if (link.startsWith("#")) {
      const id = link.slice(1);
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (link.startsWith("/")) {
      navigate(link);
    } else {
      window.open(link, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <section
      data-testid="skills-section"
      id="skills"
      className="relative bg-[#050505] border-t border-white/10"
    >
      <div className="px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <p className="overline">Доказательства</p>
            <h2 className="font-display mt-3 uppercase text-2xl sm:text-3xl lg:text-4xl leading-[1] tracking-tight">
              {site.skills_title || "Доказательства навыков"}
            </h2>
          </div>
          <p className="max-w-md text-white/55 text-sm sm:text-base leading-relaxed">
            {site.skills_subtitle || "Три точки, в которых наш подход уже даёт измеримый результат."}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5">
          {items.map((it, i) => (
            <article
              key={i}
              data-testid={`skills-banner-${i}`}
              className="group relative overflow-hidden bg-[#0a0a0a] border border-white/10 hover:border-[#a78bfa]/40 transition-colors"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                {it.image ? (
                  <img
                    src={it.image}
                    alt={it.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                  />
                ) : (
                  <div className="absolute inset-0 bbf-accent-gradient opacity-25" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute top-4 left-4 font-mono-jb text-[10px] uppercase tracking-[0.22em] text-white/65">
                  {String(i + 1).padStart(2, "0")} / 03
                </div>
              </div>
              <div className="p-6 lg:p-8 border-t border-white/10">
                <h3 className="font-display uppercase text-xl lg:text-2xl tracking-tight">
                  {it.title}
                </h3>
                <p className="mt-3 text-white/55 text-sm leading-relaxed min-h-[60px]">
                  {it.text}
                </p>
                <button
                  type="button"
                  data-testid={`skills-banner-link-${i}`}
                  onClick={() => go(it.link)}
                  className="mt-5 inline-flex items-center gap-2 font-mono-jb text-[11px] uppercase tracking-[0.22em] text-[#a78bfa] bbf-link"
                >
                  Подробнее <span aria-hidden>→</span>
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
